<template>
    <div id="app">
        <router-view />

		<!-- MountingPortal mounts here -->
		<div id="app-modal"></div>
        <div id="app-loader-layer"/>

        <dialog-error v-if="error_modal_show"
            :has_header="error_modal_has_header"
            :title="error_modal_title"
            :has_message="error_modal_has_message"
            :message="error_modal_message"
            :button_caption="error_modal_button_caption"
        />

        <dialog-success v-if="success_modal_show"
            :has_header="success_modal_has_header"
            :title="success_modal_title"
            :message="success_modal_message"
            :button_caption="success_modal_button_caption"
        />

        <dialog-confirmation v-if="confirmation_modal_show"
            :has_header="confirmation_modal_has_header"
            :is_wide="confirmation_modal_is_wide"
            :title="confirmation_modal_title"
            :message="confirmation_modal_message"
        />

        <app-movable v-if="is_show_temporary_login_timer" class="app-movable-temporary-login-timer">
            <app-timer-badge :seconds="temporary_login_timer_seconds" @timerCompleted="onTimerCompleted"/>
        </app-movable>
        
        <app-modal-hot-reload
            v-if="is_show_app_modal_hot_reload"
            @refresh="onRefresh"
            @close="onCloseAppModalHotReload"
        />
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import dynStorage from '@/utils/storage-dynamic';

import dialogError from '@/dialogs/dialog-error'
import dialogSuccess from '@/dialogs/dialog-success'
import dialogConfirmation from '@/dialogs/dialog-confirmation'

import AppTimerBadge from '@/components/app-timer-badge'
import AppMovable from '@/components/app-movable'

import AppModalHotReload from  '@/components/app-modal-hot-reload'

export default {
    components: {
        dialogError,
        dialogSuccess,
        dialogConfirmation,
        AppTimerBadge,
        AppMovable,
        AppModalHotReload,
    },

    data() {
        return {
            is_show_app_modal_hot_reload: false,
            modal_hot_reload_show_timeout_id: null,
        }
    },

	created() {
        window.addEventListener('resize', this.setViewport);

        window.addEventListener('storage', this.handleLocalStorageEvent);

        window.addEventListener('unload', this.handleUnload);
    },

    mounted() {
        if (this.hot_reload_interval) {
            this.hotReloadChecking()
        }
    },

    destroyed() {
        window.removeEventListener('resize', this.setViewport);

        window.removeEventListener('storage', this.handleLocalStorageEvent);

        window.removeEventListener('unload', this.handleUnload);
    },

    computed: {
        ...mapGetters([
            'is_auth',
            'is_account',
            'is_app_header_hidden',

            'error_modal_show',
            'error_modal_has_header',
            'error_modal_title',
            'error_modal_has_message',
            'error_modal_message',
            'error_modal_button_caption',

            'success_modal_show',
            'success_modal_has_header',
            'success_modal_title',
            'success_modal_message',
            'success_modal_button_caption',

            'confirmation_modal_show',
            'confirmation_modal_has_header',
            'confirmation_modal_is_wide',
            'confirmation_modal_title',
            'confirmation_modal_message',

            'get_catalog_services_trigger',
            'get_catalog_services_sld_trigger',

            'customer_current_mobile_product_billing_reference',
            'customer_current_mobile_product',
            'is_on_transfer_product_type',
            'is_app_support_service_intercom',

            'is_show_temporary_login_timer',
            'temporary_login_time_end',
            
            'hot_reload_interval',
        ]),

        temporary_login_timer_seconds() {
            const date_now = Date.now()

            return this.temporary_login_time_end > date_now ? +((this.temporary_login_time_end - date_now) / 1000).toFixed() : 0
        },
    },

	methods: {
		setViewport(){
			this.$store.dispatch('setViewport')
		},

        handleLocalStorageEvent(event) {
            if (!this.is_auth || !event) return

            const { key } = event

            if (key == 'customer_current_mobile_product_uuid') {
                if (this.$route.name == 'onboarding') {
                    this.$router.push({name: 'home'}).then(() => {
                        location.reload()
                    })
                } else {
                    location.reload()
                }
            }
        },

        handleUnload() {
            dynStorage.set('before_reload_route_name', this.$router?.history?.current?.name, null, true);
        },

        onTimerCompleted() {
            this.$store.commit('SET_IS_SHOW_TEMPORARY_LOGIN_TIMER', false)
            this.$store.commit('RESET_TEMPORARY_LOGIN_TIME_END')
            
            this.$router.push({name: 'home'})
                .catch(()=> {})
                .finally(()=> {
                    this.$store.dispatch('FirebaseLogout').catch(()=> {})
                })
        },
        
        hotReloadChecking() {
            this.$store.dispatch('hotReload').then(() => {
                this.is_show_app_modal_hot_reload = true
            }).catch((error) => {
                console.log('hotReloadChecking error', error)
            })
        },

        onRefresh() {
            this.is_show_app_modal_hot_reload = false

            if (this.modal_hot_reload_show_timeout_id) {
                clearTimeout(this.modal_hot_reload_show_timeout_id)

                this.modal_hot_reload_show_timeout_id = null
            }

            window.location.href = window.location.origin + window.location.pathname + (window.location.search ? window.location.search + '&' : '?') + Date.now()
        },

        onCloseAppModalHotReload() {
            this.is_show_app_modal_hot_reload = false

            this.modal_hot_reload_show_timeout_id = setTimeout(()=> {
                this.is_show_app_modal_hot_reload = true
            }, this.hot_reload_interval)
        },
	},

	watch: {
		'$route.query': {
			immediate: true,
			handler(query) {
				if (query.header === 'hide') {
					this.$store.dispatch('SetIsAppHeaderHidden', true);
				}

				if (query.platform) {
                    this.$store.dispatch('SetPlatform', query.platform);
				}

				if (query.language) {
                    this.$store.dispatch('SetLanguage', { language: query.language });
				}
			}
		},

        '$route': {
            immediate: false,
            handler(route) {
                const { path, name, params } = route

                if (!name) return;

                if (this.is_app_header_hidden && (name == 'help-center' || name == 'help-center-category' || name == 'help-center-article' || name == 'terms' || name == 'privacy' || name == 'terms-special')) return;

                this.$store.dispatch('logAnalyticsEventPageView', { path, name })

                if (this.is_app_support_service_intercom) {
                    if (document.getElementById('intercom-snippet')) return;
    
                    this.$store.dispatch('InitIntercomChat', Boolean(params?.chat));
                } else {
                    if (document.getElementById('ze-snippet')) return;
    
                    this.$store.dispatch('InitZendeskChat', Boolean(params?.chat));
                }
            }
        },

        get_catalog_services_trigger: {
            immediate: false,
            handler(curr_trigger, prev_trigger) {
                if (prev_trigger) {
                    this.$store.dispatch('GetCatalogServices', { force: true })
                }
            }
        },

        get_catalog_services_sld_trigger: {
            immediate: false,
            handler(curr_trigger, prev_trigger) {
                if (prev_trigger) {
                    this.$store.dispatch('GetCatalogServices', { force: true })
                }
            }
        },

        customer_current_mobile_product_billing_reference: {
            immediate: false,
            handler(curr_billing_reference, prev_billing_reference) {
                if (this.is_account && !this.is_on_transfer_product_type && prev_billing_reference && curr_billing_reference) {
                    const [prev_billing_reference_type, prev_billing_reference_id] = prev_billing_reference.split(':')
                    const [curr_billing_reference_type, curr_billing_reference_id] = curr_billing_reference.split(':')

                    if (prev_billing_reference_id && curr_billing_reference_id &&
                        prev_billing_reference_id === curr_billing_reference_id &&
                        prev_billing_reference_type && curr_billing_reference_type &&
                        prev_billing_reference_type !== curr_billing_reference_type) {
                            this.$store.dispatch('showSuccessMessage', { success_message: this.$t('sim_transfer_product_message'), has_header: false })

                            this.$store.commit('SET_CUSTOMER_CURRENT_PRODUCT', {
                                ...this.customer_current_mobile_product,
                            })
                    }
                }
            }
        },
	}
}
</script>

<style lang="scss">
    #app {
        --brand-color-primary: #{$brand-color-primary};
        --accent-color: #{$accent-color};
        --brand-color-white: #{$white};

        --tablet-size: #{$tablet-size};
        --mobile-size: #{$mobile-size};

        position: relative;
        overflow: hidden;

        #app-loader-layer {
            .vue-portal-target:not(:last-of-type) {
                .app-loader__layer {
                    display: none;
                }
            }
        }
    }

    .app-movable-temporary-login-timer {
        position: fixed;
        top: 92px;
        right: 24px;
        z-index: 1000;

        @include media-bp(mob) {
            top: 85px;
            right: 15px;
        }
    }
</style>
